/**
 * Credencial de sessão do admin: um par ECDSA P-256 que nasce e morre neste navegador.
 *
 * A privada é gerada com `extractable: false` e guardada como `CryptoKey` no IndexedDB,
 * então nem o JS desta página consegue exportá-la — é o que impede que uma máquina
 * infectada leve algo reutilizável. O que viaja para a API é sempre uma assinatura,
 * nunca a chave.
 */

const DB_NAME = 'px-admin-auth';
const STORE = 'session';
const RECORD_ID = 'current';

export interface SessionRecord {
  sessionId: string;
  privateKey: CryptoKey;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function run<T>(mode: IDBTransactionMode, build: (store: IDBObjectStore) => IDBRequest): Promise<T> {
  const db = await openDb();
  try {
    return await new Promise<T>((resolve, reject) => {
      const request = build(db.transaction(STORE, mode).objectStore(STORE));
      request.onsuccess = () => resolve(request.result as T);
      request.onerror = () => reject(request.error);
    });
  } finally {
    db.close();
  }
}

/** Janela privada, storage bloqueado ou dados limpos: trata como "sem sessão". */
export async function loadSession(): Promise<SessionRecord | null> {
  try {
    return (await run<SessionRecord | undefined>('readonly', store => store.get(RECORD_ID))) ?? null;
  } catch {
    return null;
  }
}

export function saveSession(record: SessionRecord) {
  return run<IDBValidKey>('readwrite', store => store.put(record, RECORD_ID));
}

export async function clearSession() {
  try {
    await run<undefined>('readwrite', store => store.delete(RECORD_ID));
  } catch {
    /* nada a limpar */
  }
}

/**
 * `extractable: false` vale para a PRIVADA; a pública é sempre exportável por
 * especificação, e é por isso que o `exportKey` abaixo funciona. Não trocar para
 * `true`: é esse flag que impede exfiltrar a credencial.
 */
export async function generateDeviceKey() {
  const pair = await crypto.subtle.generateKey(
    { name: 'ECDSA', namedCurve: 'P-256' },
    false,
    ['sign', 'verify'],
  );

  return {
    privateKey: pair.privateKey,
    publicJwk: await crypto.subtle.exportKey('jwk', pair.publicKey),
  };
}

const toBase64Url = (bytes: ArrayBuffer) =>
  btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

/**
 * JWS compacto ES256. A assinatura do WebCrypto já sai em `r||s`, que é exatamente o
 * formato que o ES256 espera — por isso o backend verifica com `jsonwebtoken` sem
 * conversão nenhuma.
 */
export async function signProof(privateKey: CryptoKey, claims: object) {
  const encoder = new TextEncoder();
  const header = toBase64Url(encoder.encode(JSON.stringify({ alg: 'ES256', typ: 'JWT' })).buffer as ArrayBuffer);
  const payload = toBase64Url(encoder.encode(JSON.stringify(claims)).buffer as ArrayBuffer);

  const signature = await crypto.subtle.sign(
    { name: 'ECDSA', hash: 'SHA-256' },
    privateKey,
    encoder.encode(`${header}.${payload}`),
  );

  return `${header}.${payload}.${toBase64Url(signature)}`;
}
